import { useState } from 'react';
import {
  BULK_IMPORT_MAX_FILES,
  BulkImportResponse,
  bulkImport
} from '../../services/adminAssets';

interface Props {
  onCancel: () => void;
  onDone: () => void;
}

const SUPPORTED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];

function isSupportedImage(file: File): boolean {
  const lower = file.name.toLowerCase();
  return SUPPORTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export default function BulkImportDialog({ onCancel, onDone }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [packName, setPackName] = useState('');
  const [packDescription, setPackDescription] = useState('');
  const [tags, setTags] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkImportResponse | null>(null);

  const acceptFiles = (incoming: FileList | File[]) => {
    const valid: File[] = [];
    const rejected: string[] = [];
    for (const f of Array.from(incoming)) {
      if (isSupportedImage(f)) valid.push(f);
      else rejected.push(f.name);
    }
    setFiles((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > BULK_IMPORT_MAX_FILES) {
        setError(`Capped at ${BULK_IMPORT_MAX_FILES} files. ${merged.length - BULK_IMPORT_MAX_FILES} dropped.`);
        return merged.slice(0, BULK_IMPORT_MAX_FILES);
      }
      if (rejected.length > 0) {
        setError(`${rejected.length} file(s) skipped (unsupported type).`);
      } else {
        setError(null);
      }
      return merged;
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) acceptFiles(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const response = await bulkImport({
        files,
        assetPackName: packName.trim() || undefined,
        assetPackDescription: packDescription.trim() || undefined,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean)
      });
      setResult(response);
    } catch {
      setError('Bulk import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded border border-neutral-800 bg-neutral-950 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold">Bulk import images</h3>
        <p className="mt-1 text-xs text-neutral-500">
          Up to {BULK_IMPORT_MAX_FILES} files. Each image gets auto-generated description + tags via GPT-4o-mini.
        </p>

        {!result ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-neutral-500">Asset pack name (optional)</label>
                <input
                  type="text"
                  value={packName}
                  onChange={(e) => setPackName(e.target.value)}
                  className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-neutral-500">Common tags (csv, optional)</label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="kenney, platformer"
                  className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
                />
              </div>
            </div>
            {packName.trim().length > 0 && (
              <div className="mt-3">
                <label className="text-xs uppercase tracking-wide text-neutral-500">Pack description (optional)</label>
                <input
                  type="text"
                  value={packDescription}
                  onChange={(e) => setPackDescription(e.target.value)}
                  className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
                />
              </div>
            )}

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`mt-4 flex flex-col items-center justify-center rounded border-2 border-dashed px-4 py-8 text-sm ${
                dragOver ? 'border-blue-500 bg-blue-900/10' : 'border-neutral-800 bg-neutral-900'
              }`}
            >
              <div className="text-neutral-400">Drag &amp; drop images here</div>
              <div className="mt-1 text-xs text-neutral-600">or</div>
              <label className="mt-2 cursor-pointer rounded border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs hover:bg-neutral-700">
                Browse files
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) acceptFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            {files.length > 0 && (
              <div className="mt-3 max-h-48 overflow-auto rounded border border-neutral-800 bg-neutral-900">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center justify-between border-b border-neutral-800 px-3 py-1.5 text-xs last:border-b-0">
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      disabled={busy}
                      className="text-neutral-500 hover:text-neutral-200"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-2 text-xs text-neutral-500">{files.length} / {BULK_IMPORT_MAX_FILES} selected</div>
            {error && <div className="mt-2 text-sm text-red-400">{error}</div>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="rounded border border-neutral-800 px-3 py-1.5 text-sm hover:bg-neutral-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={busy || files.length === 0}
                className="rounded border border-blue-700 bg-blue-700 px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? 'Importing… (no progress)' : `Import ${files.length}`}
              </button>
            </div>
          </>
        ) : (
          <BulkImportResultView result={result} onClose={onDone} />
        )}
      </div>
    </div>
  );
}

function BulkImportResultView({ result, onClose }: { result: BulkImportResponse; onClose: () => void }) {
  const uploaded = result.results.filter((r) => r.assetId && !r.isExistingAsset).length;
  const duplicated = result.results.filter((r) => r.isExistingAsset).length;
  const failed = result.results.filter((r) => r.error).length;

  return (
    <div className="mt-4 flex flex-1 flex-col overflow-hidden">
      <div className="grid grid-cols-3 gap-3 rounded border border-neutral-800 bg-neutral-900 p-3 text-center text-sm">
        <div>
          <div className="text-xl font-semibold text-green-400">{uploaded}</div>
          <div className="text-xs text-neutral-500">Uploaded</div>
        </div>
        <div>
          <div className="text-xl font-semibold text-yellow-400">{duplicated}</div>
          <div className="text-xs text-neutral-500">Duplicates</div>
        </div>
        <div>
          <div className="text-xl font-semibold text-red-400">{failed}</div>
          <div className="text-xs text-neutral-500">Failed</div>
        </div>
      </div>

      {result.totalUsage && (
        <div className="mt-2 text-xs text-neutral-500">
          LLM usage: input {result.totalUsage.inputTokens} · output {result.totalUsage.outputTokens} · ~${result.totalUsage.estimatedUsd.toFixed(4)}
        </div>
      )}

      {result.assetPackId && (
        <div className="mt-1 text-xs text-neutral-500">Asset pack id: {result.assetPackId}</div>
      )}

      <div className="mt-3 flex-1 overflow-auto rounded border border-neutral-800 bg-neutral-900">
        {result.results.map((r, i) => (
          <div key={`${r.fileName}-${i}`} className="border-b border-neutral-800 px-3 py-2 last:border-b-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{r.fileName}</div>
                {r.description && <div className="mt-0.5 truncate text-xs text-neutral-500">{r.description}</div>}
                {r.error && <div className="mt-0.5 text-xs text-red-400">{r.error}</div>}
              </div>
              <div className="shrink-0 text-xs">
                {r.error ? (
                  <span className="text-red-400">failed</span>
                ) : r.isExistingAsset ? (
                  <span className="text-yellow-400">duplicate</span>
                ) : (
                  <span className="text-green-400">uploaded</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm hover:bg-neutral-800"
        >
          Done
        </button>
      </div>
    </div>
  );
}
